import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import instance from "../../service/apiService";

// -------------------------
// TYPES
// -------------------------
export interface ClientListQueryState {
  page?: number;
  limit?: number;
  search?: string;
  scopeSelected?: string;
  selectedClientType?: string;
  search_id?: string;
  encodedCondition?: any;
}

export interface ClientListState {
  list: any[];
  total: number;
  loading: boolean;
  error: string | null;
  queries: ClientListQueryState;
}

// -------------------------
// INITIAL STATE
// -------------------------
const initialState: ClientListState = {
  list: [],
  total: 0,
  loading: false,
  error: null,
  queries: {
    page: 1,
    limit: 100,
    search: "",
    scopeSelected: "",
    selectedClientType: "",
    search_id: "",
    encodedCondition: null,
  },
};

// -------------------------
// ASYNC THUNK
// -------------------------
export const fetchClientLists = createAsyncThunk(
  "clientList/fetchClientLists",
  async (params: ClientListQueryState, { rejectWithValue }) => {
    try {
      const {
        page = 1,
        limit = 100,
        search,
        scopeSelected,
        selectedClientType,
        search_id,
        encodedCondition,
      } = params;

      let apiUrl = `${process.env.REACT_APP_BASE_URL}client?type=client_list&limit=${limit}&page=${page}`;

      if (search) apiUrl += `&search=${search}`;
      if (scopeSelected) apiUrl += `&scope=${scopeSelected}`;
      if (selectedClientType) apiUrl += `&client_type=${selectedClientType}`;
      if (search_id) apiUrl += `&search_id=${search_id}`;
      if (encodedCondition) apiUrl += `&value=${encodedCondition}`;

      const response = await instance.get(apiUrl);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// -------------------------
// SLICE
// -------------------------
const clientListSlice = createSlice({
  name: "clientList",
  initialState,
  reducers: {
    // Store all query parameters
    setClientListQueries: (
      state,
      action: PayloadAction<ClientListQueryState>
    ) => {
      state.queries = { ...state.queries, ...action.payload };
    },

    // Optional: clear filters
    clearClientListQueries: (state) => {
      state.queries = { ...initialState.queries };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchClientLists.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientLists.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload?.data || [];
        state.total = action.payload?.total || 0;
      })
      .addCase(fetchClientLists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// -------------------------
// EXPORTS
// -------------------------
export const { setClientListQueries, clearClientListQueries } =
  clientListSlice.actions;
export default clientListSlice.reducer;

// Selectors
export const selectClientListQueries = (state: any) => state.clientList.queries;
export const selectClientList = (state: any) => state.clientList.list;
export const selectClientListTotal = (state: any) => state.clientList.total;
export const selectClientListLoading = (state: any) => state.clientList.loading;
export const selectClientListError = (state: any) => state.clientList.error;
